import { useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ListTodo, Circle, Clock, CheckCircle2 } from "lucide-react";
import { TaskCard } from "@/components/tasks/TaskCard";

const STATUSES = ["Offen", "In Bearbeitung", "Erledigt"];
const ICONS = {
  "Offen": Circle,
  "In Bearbeitung": Clock,
  "Erledigt": CheckCircle2,
};

/**
 * Task board: a swipeable, scroll-snap carousel of status columns on mobile
 * (Offen -> In Bearbeitung -> Erledigt) and a 3-column grid on desktop.
 */
export function TaskBoard({ tasksByStatus, onEdit, onDelete, onStatusChange, myDayIds, onToggleMyDay, onPlanWeek }) {
  const scrollRef = useRef(null);
  const colRefs = useRef({});
  const [active, setActive] = useState("Offen");
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const findTask = (id) => STATUSES.reduce((acc, s) => acc || (tasksByStatus[s] || []).find((t) => t.id === id), null);
  const handleDrop = (e, status) => {
    e.preventDefault();
    setOverCol(null);
    const id = e.dataTransfer.getData("text/plain") || dragId;
    setDragId(null);
    const task = findTask(id);
    if (task && task.status !== status && onStatusChange) onStatusChange(task, status);
  };

  const scrollToColumn = (status) => {
    const col = colRefs.current[status];
    const cont = scrollRef.current;
    if (col && cont) {
      cont.scrollTo({ left: col.offsetLeft - cont.offsetLeft, behavior: "smooth" });
      setActive(status);
    }
  };

  const handleScroll = () => {
    const cont = scrollRef.current;
    if (!cont) return;
    const center = cont.scrollLeft + cont.clientWidth / 2;
    let nearest = STATUSES[0];
    let min = Infinity;
    STATUSES.forEach((s) => {
      const col = colRefs.current[s];
      if (col) {
        const colCenter = col.offsetLeft - cont.offsetLeft + col.clientWidth / 2;
        const d = Math.abs(colCenter - center);
        if (d < min) { min = d; nearest = s; }
      }
    });
    setActive(nearest);
  };

  return (
    <div className="space-y-3">
      {/* Mobile-only segmented switcher */}
      <div className="lg:hidden flex gap-2" data-testid="board-status-switcher">
        {STATUSES.map((s) => {
          const Icon = ICONS[s];
          const count = tasksByStatus[s]?.length || 0;
          const isActive = active === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => scrollToColumn(s)}
              className={`flex-1 min-w-0 flex items-center justify-center gap-1 rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                isActive ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground"
              }`}
              data-testid={`board-switch-${s}`}
            >
              <Icon className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{s}</span>
              <span className={`flex-shrink-0 rounded-full px-1.5 text-[10px] ${isActive ? "bg-primary-foreground/20" : "bg-muted"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Columns: swipe carousel on mobile, 3-column grid on desktop */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory gap-4 -mx-4 px-4 pb-2 no-scrollbar lg:grid lg:grid-cols-3 lg:gap-6 lg:overflow-visible lg:mx-0 lg:px-0"
        data-testid="task-board-columns"
      >
        {STATUSES.map((status) => {
          const statusTasks = tasksByStatus[status] || [];
          const StatusIcon = ICONS[status];
          return (
            <div
              key={status}
              ref={(el) => (colRefs.current[status] = el)}
              onDragOver={(e) => { e.preventDefault(); setOverCol(status); }}
              onDragLeave={() => setOverCol((c) => (c === status ? null : c))}
              onDrop={(e) => handleDrop(e, status)}
              className={`snap-center shrink-0 w-[86%] sm:w-[55%] lg:w-auto lg:shrink space-y-4 rounded-xl transition-colors ${overCol === status ? "bg-primary/5 ring-2 ring-primary/40 ring-inset p-2 -m-2" : ""}`}
              data-testid={`board-column-${status}`}
            >
              <div className="flex items-center gap-2">
                <StatusIcon className="w-5 h-5" />
                <h2 className="font-semibold">{status}</h2>
                <Badge variant="secondary" className="ml-auto">{statusTasks.length}</Badge>
              </div>

              <div className="space-y-3">
                {statusTasks.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="py-8 text-center text-muted-foreground">
                      <ListTodo className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Keine Aufgaben</p>
                    </CardContent>
                  </Card>
                ) : (
                  statusTasks.map((task) => (
                    <div
                      key={task.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData("text/plain", task.id); e.dataTransfer.effectAllowed = "move"; setDragId(task.id); }}
                      onDragEnd={() => { setDragId(null); setOverCol(null); }}
                      className={`cursor-grab active:cursor-grabbing ${dragId === task.id ? "opacity-50" : ""}`}
                      data-testid={`board-draggable-${task.id}`}
                    >
                      <TaskCard
                        task={task}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onStatusChange={onStatusChange}
                        isInMyDay={myDayIds?.has(task.id)}
                        onToggleMyDay={onToggleMyDay}
                        onPlanWeek={onPlanWeek}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
